import type { ReactNode } from "react"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconCubeOutline18 as Box,
	IconCircleCheckPlusOutline18 as CircleCheckPlus,
} from "nucleo-ui-outline-18"

import type { buttonVariantsType, PopoverProps } from "@productlane/ui"
import type { Issue, Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import { convertHtmlToText } from "@productlane/lib/src/convert-html"
import {
	Button,
	Loader,
	LucideLinearIcon,
	Popover,
	Shortcut,
	Tooltip,
	useBreakpoints,
} from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { PopoverContentProps } from "@radix-ui/react-popover"
import type { Editor } from "@tiptap/react"
import { LinearTeamsDisabled } from "@/components/linear-teams-disabled"
import { LinkButton } from "@/components/link-button"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { CreateIssueDialog } from "../../../issues/create-issue-dialog"
import { painLevelToLinearPriority } from "../../../issues/use-seed-priority-on-open"
import { CreateProjectDialog } from "../../../projects/create-project-dialog"
import { useThreadContext } from "../../thread-context"
import { IssueListItem, ProjectListItem } from "./issue-project-list-items"
import {
	CREATE_SEQUENCE_KEYS,
	createFromSequence,
	sequencePrefix,
} from "./shared/create-sequence"
import { pickSuggestions } from "./shared/picker-suggestions"
import {
	ListPreviewContext,
	useListPreviews,
} from "./shared/preview-suppression"
import { shouldShowCreateItems } from "./shared/show-create-items"
import { useSuggestedLinks } from "./thread-link-suggestions"
import { useIssueDescription } from "./use-issue-description"
import {
	linearIssueToIssue,
	useIssueProjectSearch,
} from "./use-issue-project-search"

/**
 * Combined issue and project search/select popover for linking Linear items to threads.
 * Searches both local (Zero-synced) and remote (Linear API) issues, plus local projects.
 * All results are filtered by the workspace's selected Linear team IDs.
 */

export type IssueSelectIssue = Pick<
	Issue,
	| "title"
	| "identifier"
	| "statusColor"
	| "status"
	| "statusName"
	| "description"
	| "linearTicketId"
	| "priority"
	| "dueDate"
	| "team"
	| "url"
	| "projectId"
	| "isVisible"
> &
	Partial<Pick<Issue, "assigneeId">> & {
		/** Only Linear search results carry the person; synced rows resolve the id. */
		assignee?: { name: string; avatarUrl?: string | null } | null
	} & (
		| { type: "linear" }
		| { type: "local"; id: Issue["id"]; isSelected?: boolean }
	)

export type ProjectSelectProject = Pick<
	Project,
	| "name"
	| "isVisible"
	| "description"
	| "state"
	| "color"
	| "icon"
	| "url"
	| "targetDate"
	| "sortOrder"
	| "progress"
	| "linearProjectId"
	| "id"
> &
	(
		| { type: "linear" }
		| { type: "local"; id: Project["id"]; isSelected?: boolean }
	)

interface IssueAndProjectSelectProps {
	issueIds: Array<string>
	onChange: (
		issue: IssueSelectIssue & {
			internalUpdate?: {
				isVisible?: boolean
				notUpdateVisibility: boolean
			}
		},
	) => void
	className?: string
	side?: PopoverContentProps["side"]
	align?: PopoverContentProps["align"]
	modal?: PopoverProps["modal"]
	onCloseAutoFocus?: PopoverProps["onCloseAutoFocus"]
	isHotkeyEnabled?: boolean
	isDisabled?: boolean
	shortcut?: string
	shortcutTooltip?: ReactNode
	editor?: Editor | null
	variant?: buttonVariantsType
	isOpen?: boolean
	onOpenChange?: (open: boolean) => void
	showProjects?: boolean
	projectIds?: Array<string>
	onProjectSelect?: (
		project: ProjectSelectProject & {
			internalUpdate?: {
				isVisible?: boolean
				notUpdateVisibility: boolean
			}
		},
	) => void
}

export const IssueAndProjectSelect = memo(
	({
		onChange,
		issueIds,
		className,
		side,
		align,
		modal,
		isHotkeyEnabled = true,
		onCloseAutoFocus,
		isDisabled,
		shortcut = "",
		editor,
		variant = "dashed",
		shortcutTooltip,
		isOpen: externalIsOpen,
		onOpenChange: externalOnOpenChange,
		showProjects,
		projectIds = [],
		onProjectSelect,
	}: IssueAndProjectSelectProps) => {
		const { role } = useRole()
		const { thread } = useThreadContext()
		const { isMobile } = useBreakpoints()

		// On mobile, open below instead of to the side to stay within viewport
		const responsiveSide = isMobile ? "bottom" : side

		const navigate = useNavigate()
		const [internalIsOpen, setInternalIsOpen] = useState(false)

		// Use external state if provided, otherwise use internal state
		const isPopoverOpen = externalIsOpen ?? internalIsOpen
		const setPopoverOpen = externalOnOpenChange ?? setInternalIsOpen
		const [isDialogOpen, setDialogOpen] = useState(false)
		const [isProjectDialogOpen, setProjectDialogOpen] = useState(false)
		const [searchQuery, setSearchQuery] = useState("")
		const [highlighted, setHighlighted] = useState("")

		const workspace = useZeroWorkspace()
		const suggestedLinks = useSuggestedLinks({
			threadId: thread.id,
			origin: thread.origin,
			linkedIssueIds: issueIds,
			linkedProjectIds: projectIds,
			enabled: isPopoverOpen,
		})
		const {
			debouncedQuery,
			selectedTeamIds,
			isSearchingLinear,
			issues: uniqueIssuesSorted,
			projects: filteredProjects,
			suggested,
			loadMore,
		} = useIssueProjectSearch({
			searchQuery,
			issueIds,
			projectIds,
			showProjects: !!showProjects,
			suggested: suggestedLinks,
			relatedTo: thread,
			enabled: isPopoverOpen,
		})

		const issueCreateDescription = useIssueDescription({
			highlighted,
			thread,
			slackMessages: thread?.slackMessages ?? [],
			emails: thread?.emails ?? [],
			comments: thread?.comments ?? [],
			liveChatMessages: thread?.liveChatSession?.messages ?? [],
			chatMessages: thread?.chatMessages ?? [],
		})

		const showCreateItems = shouldShowCreateItems({
			searchQuery,
			debouncedQuery,
			isFetching: isSearchingLinear,
			resultCount: uniqueIssuesSorted.length + filteredProjects.length,
		})

		const suggestions = useMemo(
			() =>
				pickSuggestions(
					uniqueIssuesSorted,
					filteredProjects,
					SUGGESTION_LIMIT,
					suggested,
				),
			[uniqueIssuesSorted, filteredProjects, suggested],
		)
		const previewRowsKey = useMemo(() => {
			const rows = searchQuery
				? [
						...filteredProjects.map((project) => project.id),
						...uniqueIssuesSorted.map((issue) => issue.linearTicketId),
					]
				: suggestions.map((suggestion) =>
						suggestion.kind === "issue"
							? suggestion.item.linearTicketId
							: suggestion.item.id,
					)
			return `${showCreateItems}:${rows.join(",")}`
		}, [
			searchQuery,
			filteredProjects,
			uniqueIssuesSorted,
			suggestions,
			showCreateItems,
		])
		const previews = useListPreviews(previewRowsKey)

		const ref = useRef<HTMLButtonElement>(null)
		const prefix = sequencePrefix(shortcut)
		const openedByShortcutAt = useRef(0)

		useShortcut("g + c", () => {
			void navigate({ to: "/edit-changelog" })
		})

		useShortcut(
			shortcut,
			(e) => {
				e.preventDefault()
				ref.current?.focus()
				openedByShortcutAt.current = Date.now()
				setPopoverOpen(true)
			},
			{
				enabled: isHotkeyEnabled && !isDisabled && role !== "VIEWER",
				enableOnContentEditable: shortcut !== "c",
				preventDefault: true,
			},
		)

		const selectIssue = (issue: IssueSelectIssue) => {
			onChange({ ...issue, internalUpdate: { notUpdateVisibility: true } })
			setSearchQuery("")
			setPopoverOpen(false)
		}

		const selectProject = (project: (typeof filteredProjects)[number]) => {
			onProjectSelect?.({
				...project,
				internalUpdate: { notUpdateVisibility: true },
			})
			setSearchQuery("")
			setPopoverOpen(false)
		}

		const openDialog = useCallback(() => {
			const selectedContent = editor
				? editor.state.doc.textBetween(
						editor.state.selection.from,
						editor.state.selection.to,
						" ",
					)
				: ""
			setHighlighted(selectedContent)
			setDialogOpen(true)
			setPopoverOpen(false)
		}, [editor, setPopoverOpen])

		const openProjectDialog = useCallback(() => {
			setProjectDialogOpen(true)
			setPopoverOpen(false)
		}, [setPopoverOpen])

		return (
			<>
				<CreateIssueDialog
					highlighted={issueCreateDescription}
					title={searchQuery || thread.title || ""}
					defaultPriority={painLevelToLinearPriority(thread.painLevel)}
					isOpen={isDialogOpen}
					onOpenChange={(dialogOpen, newIssue) => {
						if (!dialogOpen) {
							if (newIssue) {
								onChange(
									linearIssueToIssue({
										...newIssue,
										isVisible: newIssue?.internalUpdate?.isVisible,
									}),
								)
								setSearchQuery("")
								analytics.track("created-inline-issue")
							}
							setDialogOpen(false)
							analytics.track("close-inline-create-issue-dialog")
						}
					}}
				/>
				{showProjects && (
					<CreateProjectDialog
						highlighted={convertHtmlToText(thread.text, true)}
						title={searchQuery || thread.title || ""}
						isOpen={isProjectDialogOpen}
						onOpenChange={(dialogOpen, newProject) => {
							if (!dialogOpen) {
								if (newProject) {
									// Already saved under its own id by the dialog.
									onProjectSelect?.({
										type: "local",
										name: newProject.name,
										isVisible: newProject?.internalUpdate?.isVisible ?? false,
										description: newProject.description,
										state: newProject.state,
										color: newProject.color,
										icon: newProject.icon,
										url: newProject.url,
										targetDate: newProject.targetDate
											? new Date(newProject.targetDate).getTime()
											: null,
										sortOrder: newProject.sortOrder,
										progress: newProject.progress,
										linearProjectId: newProject.linearProjectId,
										id: newProject.id,
									})
									setSearchQuery("")
									analytics.track("created-inline-project")
								}
								setProjectDialogOpen(false)
								analytics.track("close-inline-create-project-dialog")
							}
						}}
					/>
				)}
				<Popover
					open={isPopoverOpen}
					onOpenChange={setPopoverOpen}
					modal={modal}
					className="w-[90vw] max-w-md p-0"
					side={responsiveSide}
					align={align}
					onCloseAutoFocus={onCloseAutoFocus}
					content={
						<Command shouldFilter={false}>
							<div className="relative">
								<CommandInput
									value={searchQuery}
									onValueChange={setSearchQuery}
									autoFocus
									placeholder="Search Linear issue or project..."
									className={cn("max-w-[21rem] md:max-w-[23rem]", {
										"max-w-[19rem] md:max-w-[21rem]": isSearchingLinear,
									})}
									disabled={selectedTeamIds.length === 0}
									onKeyDown={(event) => {
										if (!prefix || !showCreateItems) return
										const create = createFromSequence(event, {
											openedByShortcutAt: openedByShortcutAt.current,
											now: Date.now(),
											query: searchQuery,
											showProjects: !!showProjects,
										})
										if (!create) return
										event.preventDefault()
										openedByShortcutAt.current = 0
										if (create === "issue") {
											analytics.track("open-inline-create-issue-dialog")
											openDialog()
										} else {
											analytics.track("open-inline-create-project-dialog")
											openProjectDialog()
										}
									}}
								/>
								<div className="absolute right-5 top-1/2 flex -translate-y-1/2 flex-row items-center justify-between gap-2">
									{isSearchingLinear && (
										<Tooltip label="Syncing issues from Linear">
											<Loader className="mr-2 h-4 w-4" />
										</Tooltip>
									)}
									<div className="flex items-center gap-1">
										{shortcut && (
											<div className="flex items-center gap-1.5">
												<Shortcut>C</Shortcut>
											</div>
										)}
									</div>
								</div>
							</div>
							<ListPreviewContext.Provider value={previews.value}>
								<CommandList
									ref={previews.listRef}
									onScroll={(event) => {
										if (!searchQuery) return
										const list = event.currentTarget
										const remaining =
											list.scrollHeight - list.scrollTop - list.clientHeight
										if (remaining < LOAD_MORE_THRESHOLD) loadMore()
									}}
								>
									{selectedTeamIds.length === 0 && (
										<LinearTeamsDisabled
											onNavigate={() => {
												setPopoverOpen(false)
											}}
										/>
									)}
									{selectedTeamIds.length > 0 && showCreateItems && (
										<CreateCards
											showProjects={!!showProjects}
											prefix={prefix}
											onCreateIssue={openDialog}
											onCreateProject={openProjectDialog}
										/>
									)}
									{selectedTeamIds.length > 0 && !searchQuery && (
										<CommandGroup>
											{suggestions.map((suggestion) =>
												suggestion.kind === "issue" ? (
													<IssueListItem
														key={suggestion.item.linearTicketId}
														oneLine
														issueIds={issueIds}
														issue={suggestion.item}
														onSelect={() => selectIssue(suggestion.item)}
													/>
												) : (
													<ProjectListItem
														key={suggestion.item.id}
														oneLine
														project={suggestion.item}
														projectIds={projectIds}
														onSelect={() => selectProject(suggestion.item)}
													/>
												),
											)}
										</CommandGroup>
									)}
									{selectedTeamIds.length > 0 && !!searchQuery && (
										<>
											{showProjects && filteredProjects.length > 0 && (
												<CommandGroup>
													{filteredProjects.map((project) => (
														<ProjectListItem
															key={project.id}
															oneLine
															project={project}
															projectIds={projectIds}
															onSelect={() => selectProject(project)}
														/>
													))}
												</CommandGroup>
											)}
											<CommandGroup>
												{uniqueIssuesSorted.map((issue) => (
													<IssueListItem
														key={issue.linearTicketId}
														oneLine
														issueIds={issueIds}
														issue={issue}
														onSelect={() => selectIssue(issue)}
													/>
												))}
											</CommandGroup>
										</>
									)}
								</CommandList>
							</ListPreviewContext.Provider>
						</Command>
					}
				>
					{!hasLinearConnection(workspace) ? (
						<Tooltip label="Connect Linear to link threads to issues.">
							<LinkButton
								to="/settings/integrations/linear"
								icon={LucideLinearIcon}
								iconClassName="h-3.5 w-3.5"
								variant="dashed"
								className="rounded-full"
								disabled
							>
								Linear
							</LinkButton>
						</Tooltip>
					) : (
						<div>
							<Tooltip
								label={shortcut ? shortcutTooltip : "Link or create issue"}
							>
								<Button
									variant={variant}
									Icon={LucideLinearIcon}
									className={cn(
										className,
										"relative flex h-6 min-h-[1.75rem] max-w-xs flex-none select-none items-center justify-center gap-2 rounded-full pl-2 pr-2.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-600 disabled:pointer-events-none",
									)}
									disabled={isDisabled}
									ref={ref}
									size="sm"
								>
									Linear
								</Button>
							</Tooltip>
						</div>
					)}
				</Popover>
			</>
		)
	},
)

const SUGGESTION_LIMIT = 5
// Pixels from the bottom at which the next page starts loading.
const LOAD_MORE_THRESHOLD = 240

const CREATE_CARD =
	"flex-col items-stretch gap-0 rounded-lg border border-gray-200 bg-white p-3 aria-selected:border-gray-300 aria-selected:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/60 dark:aria-selected:border-gray-600 dark:aria-selected:bg-gray-800"

export const CreateCards = memo(
	({
		showProjects,
		prefix,
		onCreateIssue,
		onCreateProject,
	}: {
		showProjects: boolean
		prefix: string | null | undefined
		onCreateIssue: () => void
		onCreateProject: () => void
	}) => {
		const cards = [
			{
				value: "issue-create-issue",
				Icon: CircleCheckPlus,
				label: "Create issue",
				key: CREATE_SEQUENCE_KEYS.issue,
				onSelect: () => {
					analytics.track("open-inline-create-issue-dialog")
					onCreateIssue()
				},
			},
			...(showProjects
				? [
						{
							value: "project-create-project",
							Icon: Box,
							label: "Create project",
							key: CREATE_SEQUENCE_KEYS.project,
							onSelect: () => {
								analytics.track("open-inline-create-project-dialog")
								onCreateProject()
							},
						},
					]
				: []),
		]
		return (
			<CommandGroup
				className={cn(
					"[&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:gap-1.5",
					cards.length > 1 && "[&_[cmdk-group-items]]:grid-cols-2",
				)}
			>
				{cards.map(({ value, Icon, label, key, onSelect }) => (
					<CommandItem
						key={value}
						value={value}
						onSelect={onSelect}
						className={CREATE_CARD}
					>
						<Icon className="mb-3 h-[18px] w-[18px] flex-none text-secondary group-aria-selected:text-primary" />
						<div className="text-sm text-primary">{label}</div>
						{prefix && (
							<div className="mt-1.5 flex items-center gap-1.5">
								<Shortcut variant="default">{prefix}</Shortcut>
								<span className="text-xs text-gray-500">then</span>
								<Shortcut variant="default">{key}</Shortcut>
							</div>
						)}
					</CommandItem>
				))}
			</CommandGroup>
		)
	},
)
